/**
 * AI Recruit - Assignee Selector Component
 * Multi-select dropdown for selecting job assignees during job creation
 */

import React, { useState, useRef, useEffect } from 'react'
import { XMarkIcon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useOrganizationUsers } from '../../hooks/useAssignment'
import type { OrganizationUser } from '../../services/assignmentApi'
import LoadingSpinner from '../common/LoadingSpinner'

export interface AssigneeSelectorProps {
  selectedAssignees: OrganizationUser[]
  onChange: (assignees: OrganizationUser[]) => void
  currentUser: { id: string; full_name: string; email: string; role: string }
  error?: string
  disabled?: boolean
}

const AssigneeSelector: React.FC<AssigneeSelectorProps> = ({
  selectedAssignees,
  onChange,
  currentUser,
  error,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Fetch users with debounced search
  const { 
    data: usersData, 
    isLoading, 
    isError 
  } = useOrganizationUsers(searchTerm, isOpen)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Filter out already-selected users from dropdown results (client-side)
  const selectedIds = new Set(selectedAssignees.map(u => u.id))
  const availableUsers = usersData?.users?.filter(user => !selectedIds.has(user.id)) || []

  // On API error, fall back to showing only current user if not already selected
  const fallbackUsers = isError && !selectedIds.has(currentUser.id) 
    ? [currentUser] 
    : []

  const displayUsers = isError ? fallbackUsers : availableUsers

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('')
      }
    }
  }

  const handleSelectUser = (user: OrganizationUser) => {
    onChange([...selectedAssignees, user])
    setSearchTerm('')
    // Keep dropdown open for multi-select
  }

  const handleRemoveUser = (userId: string) => {
    if (!disabled) {
      onChange(selectedAssignees.filter(u => u.id !== userId))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        Assign To
      </label>
      
      <div className="relative" ref={dropdownRef}>
        {/* Selected users as chips */}
        <div
          className={`min-h-[42px] w-full px-3 py-2 border rounded-md shadow-sm bg-white flex flex-wrap gap-2 items-center cursor-pointer ${
            error
              ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500'
              : 'border-gray-300 focus-within:ring-primary-500 focus-within:border-primary-500'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          onClick={handleToggle}
        >
          {selectedAssignees.length > 0 ? (
            selectedAssignees.map(user => (
              <span
                key={user.id}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded-md"
              >
                {user.full_name}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveUser(user.id)
                  }}
                  disabled={disabled}
                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={`Remove ${user.full_name}`}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </span>
            ))
          ) : (
            <span className="text-gray-400 text-sm">Select assignees...</span>
          )}
          
          <ChevronDownIcon 
            className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </div>

        {/* Dropdown menu */}
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search users..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  aria-label="Search users"
                />
              </div>
            </div>

            {/* User list */}
            <ul className="max-h-60 overflow-y-auto py-1" role="listbox">
              {isLoading ? (
                <li className="flex justify-center py-4">
                  <LoadingSpinner />
                </li>
              ) : isError ? (
                <li className="px-4 py-3 text-sm text-red-500">
                  Error loading users. {fallbackUsers.length > 0 ? 'Showing current user only.' : ''}
                </li>
              ) : displayUsers.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500 italic">
                  {searchTerm ? 'No users found' : 'All users already selected'}
                </li>
              ) : (
                displayUsers.map(user => (
                  <li
                    key={user.id}
                    role="option"
                    aria-selected={false}
                    onClick={() => handleSelectUser(user)}
                    className="px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{user.full_name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded ml-2">
                        {user.role}
                      </span>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}

export default AssigneeSelector
